import React, { useCallback, useMemo } from 'react';

import { Box } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';
import { useIntl } from 'react-intl';

import { IBackendCartEntries, IHistoricalOrder } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonVariants } from 'components/action-button';
import useErrorModal from 'hooks/use-error-modal';
import useOrderNumber from 'hooks/use-order-number';
import { useOrderContext } from 'state/order';
import { useReorder } from 'state/order/hooks/use-reorder';
import { useCreateOrderAriaLabel } from 'utils/accessibility/aria';
import { routes } from 'utils/routing';

import OrderAction from './order-action';
import { RewardText } from './reward-text';
import {
  Col,
  FormattedDate,
  ItemNameContainer,
  Items,
  MoreItems,
  MoreItemsText,
  NumItems,
  OrderBox,
  OrderNumber,
  OrderNumberText,
  OrderNumberWrapper,
  SubtitleContainer,
  Tile,
} from './styled';

interface IOrderProps {
  order: IHistoricalOrder;
}

const Order = ({ order }: IOrderProps) => {
  const {
    reorder: { reorderedOrderId },
  } = useOrderContext();
  const { store, fetchStore } = useStoreContext();
  const { handleReorderClick, isReorderingDisabled } = useReorder(order);
  const { formatMessage } = useIntl();

  const router = useRouter();

  // @ts-ignore
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Fetch Store Failure',
  });
  const formattedDate = useMemo(
    () =>
      new Date(order.createdAt).toLocaleString(formatMessage({ id: 'localeDateTime' }), {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      }),
    [formatMessage, order.createdAt]
  );

  const { itemCSV, numItems, rewardRedeemed } = useMemo(() => {
    const cartEntries = order.cart.cartEntries || [];
    const csvForItem = cartEntries.map((entry: IBackendCartEntries) => entry.name).join(', ');
    const totalItemsOrdered = cartEntries.reduce(
      (acc: number, item: IBackendCartEntries) => acc + item.quantity,
      0
    );

    const itemsCount = `${totalItemsOrdered} ${(totalItemsOrdered === 1
      ? formatMessage({ id: 'item' })
      : formatMessage({ id: 'items' })
    ).toLowerCase()}`;

    // order redeemed a reward if an entry has `redeemable` as true
    const wasRewardRedeemed = cartEntries.some((entry: IBackendCartEntries) => entry.redeemable);

    return { itemCSV: csvForItem, numItems: itemsCount, rewardRedeemed: wasRewardRedeemed };
  }, [order, formatMessage]);

  const showOrderModal = useCallback(() => {
    router.navigate(routes.orderDetails(order.rbiOrderId));
  }, [order.rbiOrderId, router]);

  const handleFinishOrder = async () => {
    const orderStoreId = order.cart?.storeId ? order.cart.storeId.toString() : '';

    // Store needs to be re-fetched if the current one is different is missing.
    if (store.number && store.number !== orderStoreId) {
      try {
        await fetchStore(orderStoreId);
      } catch {
        return openErrorDialog({ message: formatMessage({ id: 'couldNotLocateStore' }) });
      }
    }
    router.replace(`${routes.orderConfirmation}/${order.rbiOrderId}`);
  };

  // Show the first item and tell the user how many other items there is in the order
  const itemsArr = itemCSV.split(',');
  const remainingItems = itemsArr.length - 1;
  const renderMoreItems = useCallback(
    () => (
      <MoreItems onPress={() => showOrderModal()}>
        <MoreItemsText>{formatMessage({ id: 'moreItems' }, { remainingItems })}</MoreItemsText>
      </MoreItems>
    ),
    [formatMessage, remainingItems, showOrderModal]
  );
  const formatedItemsList = useMemo(
    () => [itemsArr[0], ...(remainingItems > 0 ? [renderMoreItems()] : [])],
    [itemsArr, remainingItems, renderMoreItems]
  );

  const createAriaLabel = useCreateOrderAriaLabel();
  const orderNumber = useOrderNumber(order);

  const showTrackOrderModalPage = () =>
    router.navigate(`${routes.orderConfirmation}/${order.rbiOrderId}?dismissOnly=true`);

  return (
    <>
      <Tile testID={`recent-order-${order.rbiOrderId}`}>
        <Col focusable>
          <OrderNumberWrapper>
            {orderNumber && (
              <>
                <OrderNumberText variant="headerTwo">{`${formatMessage({
                  id: 'orderNumber',
                })}:`}</OrderNumberText>
                <OrderNumber variant="headerTwo">{orderNumber}</OrderNumber>
              </>
            )}
          </OrderNumberWrapper>
          <FormattedDate variant="headerTwo">{formattedDate}</FormattedDate>
          <SubtitleContainer variant="headerThree">
            <NumItems>{numItems}</NumItems>
          </SubtitleContainer>
          <ItemNameContainer>
            {rewardRedeemed && <RewardText text={formatMessage({ id: 'rewards' })} />}
            <Items>{formatedItemsList}</Items>
          </ItemNameContainer>
        </Col>
        <Col>
          <Box width="full">
            <ActionButton
              testID="recent-order-view-details"
              variant={ActionButtonVariants.OUTLINE}
              fullWidth
              onPress={showOrderModal}
              aria-label={createAriaLabel({
                buttonType: 'view',
                order,
              })}
            >
              {formatMessage({ id: 'viewDetails' })}
            </ActionButton>
            <OrderBox>
              <OrderAction
                order={order}
                loading={reorderedOrderId === order.rbiOrderId}
                disabled={isReorderingDisabled}
                onTrack={showTrackOrderModalPage}
                onReorder={handleReorderClick}
                onFinish={handleFinishOrder}
              />
            </OrderBox>
          </Box>
        </Col>

        <ErrorDialog />
      </Tile>
    </>
  );
};

export default Order;
